import { action, makeObservable, observable, runInAction } from 'mobx';
import { Injectable } from './service.base';
import Cookies from 'js-cookie';

@Injectable()
export class CodeVerificationService {
    @observable public isCodeExpired = false;
    @observable public codeResendForbidden = false;
    @observable public isCodeInvalid = false;

    @observable public codeLeftSeconds = 0;
    @observable public resendCodeInvalidTimes = 0;

    @observable public verificationCode = '';
    private lastSubmittedCode = '';
    private interval: ReturnType<typeof setInterval> | undefined;
    private verifyInProgress = false;

    constructor() {
        makeObservable(this);
    }

    @action
    setCodeExpired = (value: boolean) => {
        this.isCodeExpired = value;
    };

    @action
    setVerificationCode = (value: string, onEnterVerificationCode?: any) => {
        this.setCodeExpired(false);
        const filtered = value.replace(/[^0-9]/g, '').slice(0, 6);
        const changed = filtered !== this.verificationCode;

        this.verificationCode = filtered;
        this.isCodeInvalid = false;

        if (filtered.length === 0) {
            this.lastSubmittedCode = '';
        }

        if (
            filtered.length === 6 &&
            filtered !== this.lastSubmittedCode &&
            typeof onEnterVerificationCode === 'function' &&
            !this.verifyInProgress
        ) {
            this.maybeVerifyCode(onEnterVerificationCode);
        }
    };

    @action
    onSingleVerificationCodeChange = (value: string, onEnterVerificationCode?: any) => {
        this.setVerificationCode(value, onEnterVerificationCode);
    };

    @action
    onSingleVerificationCodeKeyUp = async (e: React.KeyboardEvent<HTMLInputElement>, onEnterVerificationCode: any) => {
        if (
            (e.key === 'Enter' &&
                this.verificationCode.length === 6 &&
                this.verificationCode !== this.lastSubmittedCode) ||
            (this.verificationCode.length === 6 &&
                this.verificationCode !== this.lastSubmittedCode &&
                e.key.length === 1)
        ) {
            await this.maybeVerifyCode(onEnterVerificationCode);
        }
    };

    @action
    onSingleVerificationCodePaste = async (e: React.ClipboardEvent<HTMLInputElement>, onEnterVerificationCode: any) => {
        e.preventDefault();
        const pasteText: string = e.clipboardData.getData('Text');
        const filtered = pasteText.replace(/[^0-9]/g, '').slice(0, 6);
        this.setVerificationCode(filtered, onEnterVerificationCode);
    };

    @action
    maybeVerifyCode = async (onEnterVerificationCode: any) => {
        if (
            this.verificationCode.length === 6 &&
            this.verificationCode !== this.lastSubmittedCode &&
            !this.verifyInProgress
        ) {
            await this.verifyCode(onEnterVerificationCode);
        }
    };

    @action
    async verifyCode(onEnterVerificationCode: any) {
        if (this.verificationCode.length !== 6) {
            this.setCodeInvalid(true);
            return;
        }

        this.verifyInProgress = true;
        this.lastSubmittedCode = this.verificationCode;
        this.setCodeInvalid(false);

        const verifyCodeResult: boolean = await onEnterVerificationCode(this.verificationCode);
        clearInterval(this.interval);

        runInAction(() => {
            this.verifyInProgress = false;
            if (!verifyCodeResult) {
                this.setCodeInvalid(true);
                this.setCodeLeftSeconds(0);
            }
        });
    }

    @action
    resendTimer = (seconds: number) => {
        if (this.interval) {
            clearInterval(this.interval);
        }
        this.codeLeftSeconds = seconds;
        this.setCodeExpired(false);

        this.interval = setInterval(() => {
            if (this.codeLeftSeconds > 0) {
                this.decrementCodeLeftSeconds();
            } else {
                clearInterval(this.interval);
                this.setCodeExpired(true);
            }
        }, 1000);
    };

    @action
    decrementCodeLeftSeconds = () => {
        this.codeLeftSeconds--;
    };

    @action
    setCodeResendForbidden = (value: boolean) => {
        this.codeResendForbidden = value;
    };

    @action
    setCodeLeftSeconds = (value: number) => {
        this.codeLeftSeconds = value;
    };

    @action
    setCodeInvalid = (value: boolean) => {
        this.isCodeInvalid = value;
        if (this.isCodeInvalid) {
            this.resendCodeInvalidTimes++;
            if (this.resendCodeInvalidTimes > 4) {
                const expMinutes = 10;
                const date = new Date();
                date.setTime(date.getTime() + expMinutes * 60 * 1000);
                Cookies.set('delayResendCookie', 'Yes', { path: '/', expires: date });
                this.setCodeResendForbidden(true);
            }
        }
    };

    @action
    handleResendClick = async (e: any, onSendVerificationCode: any) => {
        e.preventDefault();
        const resendResult = await onSendVerificationCode();
        if (resendResult) {
            this.initSendVerificationCode();
        }
    };

    @action
    initSendVerificationCode = () => {
        this.setCodeInvalid(false);
        this.verificationCode = '';
        this.lastSubmittedCode = '';
        const delayResendCookie = Cookies.get('delayResendCookie');
        if (!delayResendCookie) {
            this.resendTimer(120);
        } else {
            this.setCodeResendForbidden(true);
        }
    };
}
